import { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { CONVERSATION_SELECT_PARTIAL_PROPS, DEFAULT_ASSIGNMENT_FILTER, DEFAULT_STATUS_FILTER } from '@/components/inbox/types'
import type { ConversationListItem, Team } from '@/components/inbox/types'

export function appendFilterParams(params: URLSearchParams, aFilter: string, sFilter: string, tFilter: string | null, cFilter: string | null = null) {
  if (aFilter !== DEFAULT_ASSIGNMENT_FILTER) params.set('filter', aFilter)
  if (sFilter !== DEFAULT_STATUS_FILTER) params.set('status', sFilter)
  if (tFilter) params.set('team', tFilter)
  if (cFilter) params.set('channel', cFilter)
}

interface UseConversationListParams {
  initialConversations: ConversationListItem[]
  initialHasMore: boolean
  initialCurrentPage: number
  initialTeams: Team[]
  initialStats: { all: number; assigned_to_me: number; unassigned: number; open: number; closed: number; snoozed: number }
  filter: string
  statusFilter: string
  teamFilter: string | null
  channelFilter: string | null
  currentAgentId: number
  conversations: ConversationListItem[]
  setConversations: React.Dispatch<React.SetStateAction<ConversationListItem[]>>
  setStats: React.Dispatch<React.SetStateAction<{ all: number; assigned_to_me: number; unassigned: number; open: number; closed: number; snoozed: number }>>
  setTeams: React.Dispatch<React.SetStateAction<Team[]>>
  setSelectedConversationIds: React.Dispatch<React.SetStateAction<Set<number>>>
  selectedConversationId: number | null
  isComposingNew: boolean
  cancelNewConversation: () => void
}

export function useConversationList({
  initialConversations,
  initialHasMore,
  initialCurrentPage,
  initialTeams,
  initialStats,
  filter,
  statusFilter,
  teamFilter,
  channelFilter,
  currentAgentId,
  conversations,
  setConversations,
  setStats,
  setTeams,
  setSelectedConversationIds,
  selectedConversationId,
  isComposingNew,
  cancelNewConversation,
}: UseConversationListParams) {
  const [searchQuery, setSearchQuery] = useState('')
  const [hasMore, setHasMore] = useState(initialHasMore)
  const [currentPage, setCurrentPage] = useState(initialCurrentPage)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Build URL with filter params
  const buildFilterUrl = useCallback(
    (conversationId?: number | null, assignmentFilter?: string, status?: string, teamId?: string | null, channel?: string | null) => {
      const params = new URLSearchParams()
      appendFilterParams(params, assignmentFilter ?? filter, status ?? statusFilter, teamId !== undefined ? teamId : teamFilter, channel !== undefined ? channel : channelFilter)
      const queryString = params.toString()
      const basePath = conversationId ? `/inbox/${conversationId}` : '/inbox'
      return `${basePath}${queryString ? `?${queryString}` : ''}`
    },
    [filter, statusFilter, teamFilter, channelFilter]
  )

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      router.visit(buildFilterUrl(selectedConversationId, filter, newStatus), {
        preserveState: true,
        preserveScroll: true,
      })
    },
    [buildFilterUrl, selectedConversationId, filter]
  )

  // Filter conversations by search
  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      conv.end_user.name.toLowerCase().includes(query) ||
      conv.end_user.email?.toLowerCase().includes(query) ||
      conv.end_user.company?.toLowerCase().includes(query) ||
      conv.last_message?.toLowerCase().includes(query)
    )
  })

  // Check if conversation matches current filter
  const matchesCurrentFilter = useCallback((conv: ConversationListItem) => {
    const isAssignedToMe = conv.assigned_agent?.id === currentAgentId
    const isUnassigned = conv.assigned_agent === null
    const matchesStatus = conv.status === statusFilter
    if (!matchesStatus) return false
    if (teamFilter && String(conv.team_id) !== teamFilter) return false
    if (channelFilter === 'whatsapp' && conv.source !== 'whatsapp') return false
    if (channelFilter === 'chat' && conv.source === 'whatsapp') return false
    switch (filter) {
      case 'assigned_to_me': return isAssignedToMe
      case 'unassigned': return isUnassigned
      case 'all':
      default: return true
    }
  }, [filter, statusFilter, teamFilter, channelFilter, currentAgentId])

  // Restore team filter from localStorage
  const hasRestoredTeamFilter = useRef(false)
  useEffect(() => {
    if (teamFilter) { hasRestoredTeamFilter.current = true; return }
    if (hasRestoredTeamFilter.current) return
    hasRestoredTeamFilter.current = true
    if (initialTeams.length === 0) return
    const savedTeam = localStorage.getItem('inbox_team_filter')
    if (!savedTeam) return
    if (!initialTeams.some((t) => String(t.id) === savedTeam)) return
    const params = new URLSearchParams(window.location.search)
    params.set('team', savedTeam)
    router.visit(`${window.location.pathname}?${params.toString()}`, { preserveState: true, replace: true })
  }, [])

  // Persist team filter
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return }
    if (teamFilter) {
      localStorage.setItem('inbox_team_filter', teamFilter)
    } else {
      localStorage.removeItem('inbox_team_filter')
    }
  }, [teamFilter])

  // Update conversations list from props
  useEffect(() => {
    setConversations(initialConversations)
    setStats(initialStats)
    setTeams(initialTeams)
    setHasMore(initialHasMore)
    setCurrentPage(initialCurrentPage)
    setSelectedConversationIds(new Set())
  }, [initialConversations, initialStats, initialTeams, initialHasMore, initialCurrentPage])

  // Infinite scroll
  const loadMoreConversations = useCallback(async () => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(true)
    const nextPage = currentPage + 1
    try {
      const params = new URLSearchParams()
      params.set('page', nextPage.toString())
      appendFilterParams(params, filter, statusFilter, teamFilter, channelFilter)
      const response = await fetch(`/inbox/conversations?${params.toString()}`)
      if (!response.ok) throw new Error('Failed to load more conversations')
      const data = await response.json()
      setConversations((prev) => {
        const existingIds = new Set(prev.map((c) => c.id))
        const newConversations = data.conversations.filter(
          (c: ConversationListItem) => !existingIds.has(c.id)
        )
        return [...prev, ...newConversations]
      })
      setHasMore(data.hasMore)
      setCurrentPage(data.currentPage)
    } catch (error) {
      console.error('Error loading more conversations:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, hasMore, currentPage, filter, statusFilter, teamFilter, channelFilter])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !searchQuery) {
          loadMoreConversations()
        }
      },
      { threshold: 0.1 }
    )
    if (loadMoreRef.current) observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, searchQuery, loadMoreConversations])

  function handleConversationClick(conversationId: number) {
    if (isComposingNew) cancelNewConversation()
    setConversations((prev) => prev.map((c) => c.id === conversationId ? { ...c, unread_count: 0 } : c))
    router.visit(buildFilterUrl(conversationId), {
      preserveState: true,
      preserveScroll: true,
      only: CONVERSATION_SELECT_PARTIAL_PROPS,
    })
  }

  return {
    searchQuery,
    setSearchQuery,
    filteredConversations,
    matchesCurrentFilter,
    buildFilterUrl,
    handleStatusChange,
    handleConversationClick,
    hasMore,
    isLoadingMore,
    loadMoreRef,
    loadMoreConversations,
  }
}
